/**
 * Pedaço que sumiu depois de um deploy novo ou de uma volta (pré-mortem de 25/10, ORD-F-146). O Hosting só serve os
 * arquivos da versão no ar: quem está com a aba aberta numa versão e pede um pedaço sob demanda dela (o mapa às 17h00,
 * a chance, a página inicial) recebe 404 depois de um `firebase deploy` ou de um `hosting:clone`. Numa falha de
 * `import()` (o evento `vite:preloadError` e as falhas dos carregadores, `chunkFailed`), a página busca `/` sem cache e
 * compara o build no ar com o seu; se mudou, recarrega, uma vez por build (a mesma trava de sessão do
 * `ops.reload_build`, sem laço). Se não mudou, é falha de rede: nada aqui, e as boundaries tentam de novo.
 */
import { fetchLivePage } from '../data/livePage';
import { bindChunkFailure } from '../state/actions';
import { claimReload } from '../state/storage';

/** O build no `#app-config` do HTML (`"build":"<commit>-<data>"`). */
const BUILD_RE = /"build":"([^"\\]+)"/;

export interface StaleBuildDeps {
  /** O HTML de `/`, sem cache; null em falha. */
  fetchText: (url: string) => Promise<string | null>;
  reload: () => void;
  /** A trava de sessão: true só na 1ª vez por chave. */
  claim: (key: string) => boolean;
}

const BROWSER: StaleBuildDeps = {
  fetchText: fetchLivePage,
  reload: () => location.reload(),
  claim: claimReload,
};

/** Pura o bastante para teste: true se recarregou (o build no ar é outro e a trava deixou). */
export async function recoverIfStale(current: string, deps: StaleBuildDeps = BROWSER): Promise<boolean> {
  const html = await deps.fetchText(`/?build=${Date.now()}`);
  const live = html ? (BUILD_RE.exec(html)?.[1] ?? null) : null;
  if (live === null || live === current) return false;
  if (!deps.claim(`stale:${live}`)) return false;
  deps.reload();
  return true;
}

let current: string | null = null;
let deps: StaleBuildDeps = BROWSER;
let inflight: Promise<boolean> | null = null;

/**
 * Um pedaço sob demanda falhou (os carregadores do app chamam; as features, pela ação `chunkFailed` de state/actions.ts,
 * que a instalação liga a esta): confere o build no ar uma vez por vez.
 */
export function chunkFailed(): void {
  if (current === null || inflight !== null) return;
  inflight = recoverIfStale(current, deps).finally(() => {
    inflight = null;
  });
}

/** Instala a recuperação (main.tsx e a entrada do replay), com o build desta página. */
export function installStaleBuildRecovery(build: string, win: Window = window, d: StaleBuildDeps = BROWSER): void {
  current = build;
  deps = d;
  bindChunkFailure(chunkFailed);
  if (typeof win.addEventListener === 'function') win.addEventListener('vite:preloadError', chunkFailed);
}
